import { useState } from 'react';
import { motion } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';
import { FAQ } from '../components/FAQ';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { BookingModal } from '../components/BookingModal';
import { Link } from 'react-router-dom';
import { 
  Car, Home, Clock, Star,
  CheckCircle, Phone, ArrowRight, MapPin
} from 'lucide-react';
import { ContactDropdown } from '../components/ContactDropdown';

export const AutoDetailingPage = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const allPackagesUrl = 'https://widget.zenbooker.com/book/1763871181776x633987278981962200?embed=true&fullpage=true';
  const [selectedPackage, setSelectedPackage] = useState(1);

  const openBooking = (packageLevel) => {
    setSelectedPackage(packageLevel);
    setIsBookingOpen(true);
  };
  const benefits = [
    { icon: <Home className="w-6 h-6" />, title: 'We Come To You', description: 'Professional detailing at your home, office, or any convenient location in the Fresno area.' },
    { icon: <Clock className="w-6 h-6" />, title: 'Save Your Time', description: 'No need to drop off your car - continue your day while we work.' },
    { icon: <Star className="w-6 h-6" />, title: 'Premium Results', description: 'Same professional-grade products and equipment as high-end detail shops.' },
    { icon: <Car className="w-6 h-6" />, title: 'All Vehicle Types', description: 'Cars, trucks, SUVs, boats, RVs - we detail them all with care.' },
  ];

  const services = [
    {
      title: 'Exterior Detailing',
      items: ['Hand wash & foam bath', 'Clay bar treatment', 'Wheel & tire cleaning', 'Paint sealant/wax', 'Window cleaning', 'Trim restoration']
    },
    {
      title: 'Interior Detailing',
      items: ['Deep vacuum', 'Leather conditioning', 'Carpet extraction', 'Dashboard & console cleaning', 'Odor elimination', 'Steam sanitization']
    },
    {
      title: 'Add-On Services',
      items: ['Engine bay cleaning', 'Headlight restoration', 'Pet hair removal', 'Fabric protection', 'Ceramic coating', 'Paint correction']
    }
  ];

  const faqItems = [
    {
      question: 'How does mobile detailing work?',
      answer: 'We bring all professional equipment to your location including power, water tanks, vacuums, and products. Simply provide a parking spot with some space around the vehicle, and we handle the rest. No access to water or power needed from you!'
    },
    {
      question: 'How much does mobile detailing cost in Fresno?',
      answer: 'Our mobile detailing services start at $99 for the Maintenance Refresh package. Full details range from $150-$230+ depending on vehicle size and condition. We offer transparent pricing with no hidden fees. Call 559-880-1818 for an exact quote.'
    },
    {
      question: 'What areas do you serve for mobile detailing?',
      answer: 'We provide mobile auto detailing throughout Central California including Fresno, Madera, Clovis, Chowchilla, Sanger, Selma, and surrounding communities. Contact us if you\'re outside these areas - we may still be able to accommodate!'
    },
    {
      question: 'How long does a full detail take?',
      answer: 'Service times vary by package and vehicle condition. Our Maintenance Refresh takes 1-2 hours, Full Detail takes 3-4 hours, and Elite packages can take 5-6+ hours. We never rush to ensure quality results.'
    },
    {
      question: 'Do I need to be home during the detail?',
      answer: 'No! Many clients schedule details while at work or running errands. As long as we have access to your vehicle and a suitable workspace, you\'re free to go about your day. We\'ll text you before/after photos when complete.'
    },
    {
      question: 'Can you remove pet hair and odors?',
      answer: 'Absolutely! Pet hair removal is one of our specialties. We use professional extraction equipment and techniques to remove embedded pet hair from carpets, seats, and crevices. Our steam cleaning and odor elimination treatments effectively neutralize pet odors.'
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Mobile Auto Detailing Service Fresno",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Noble Detailing",
      "telephone": "559-880-1818",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Fresno",
        "addressRegion": "CA"
      }
    },
    "areaServed": ["Fresno", "Madera", "Clovis", "Chowchilla"],
    "description": "Professional mobile auto detailing services in Fresno, CA. Interior and exterior detailing at your location."
  };

  return (
    <>
      <SEOHead
        title="Mobile Auto Detailing Fresno | We Come To You | Noble Detailing"
        description="Professional mobile auto detailing in Fresno, Madera & Clovis. Full interior & exterior detailing at your location. Starting at $99. Call 559-880-1818 for booking."
        keywords="mobile detailing fresno, car detailing fresno, auto detailing madera, mobile car wash clovis, interior detailing fresno, exterior detailing fresno, car cleaning chowchilla, mobile auto detail near me, professional car detailing central california"
        canonical="https://nobledetailing.com/mobile-detailing-fresno"
        schema={schema}
      />

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingOpen} 
        onClose={() => setIsBookingOpen(false)} 
        customUrl={allPackagesUrl}
      />

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center pt-20" data-testid="detailing-hero">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1709346501471-491c684db831?w=1920&q=80"
            alt="Mobile auto detailing foam wash service in Fresno"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
              <span className="text-gray-600">/</span>
              <span className="text-cyan-400">Mobile Detailing</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight uppercase tracking-wide">
              Mobile Auto Detailing<br />
              <span className="text-cyan-400">Fresno & Central CA</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Professional auto detailing delivered to your doorstep. <strong>We bring the detail shop to you</strong> - at your home, office, or anywhere in <strong>Fresno, Madera, Clovis, Chowchilla</strong> and surrounding areas. Starting at just <strong>$99</strong>.
            </p>

            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => setIsBookingOpen(true)}
                className="btn-primary flex items-center gap-2"
                data-testid="detailing-book-btn"
              >
                Book Now <ArrowRight className="w-5 h-5" />
              </button>
              <ContactDropdown 
                label="Contact Us" 
                className="btn-secondary"
                testId="detailing-contact-cta"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-[#0a0a0a]" data-testid="detailing-benefits">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-4">
              Why Mobile Detailing?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Get professional-quality detailing without leaving your home or office. We bring everything needed for a showroom-quality detail.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8 bg-neutral-900/50 border border-white/5 hover:border-cyan-500/30 transition-all"
              >
                <div className="w-14 h-14 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mx-auto mb-6">
                  {benefit.icon}
                </div>
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wider mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding" data-testid="detailing-services">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-4">
              Detailing Services
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Comprehensive interior and exterior detailing services for all vehicle types in Fresno and surrounding areas.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-neutral-900 border border-white/10 p-8 hover:border-cyan-500/30 transition-all"
              >
                <h3 className="font-heading text-xl font-semibold uppercase tracking-wider mb-6 text-cyan-400">
                  {service.title}
                </h3>
                <ul className="space-y-3">
                  {service.items.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-gray-300">
                      <CheckCircle className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="section-padding bg-[#0a0a0a]" data-testid="detailing-gallery">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-4">
              Detailing Results
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              See the transformation our mobile detailing team delivers to vehicles throughout Fresno and Central California.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div>
              <p className="text-cyan-400 text-sm uppercase tracking-wider mb-4 font-semibold">
                Pet Hair Removal
              </p>
              <BeforeAfterSlider
                beforeImage="/gallery/detailing/before.jpg"
                afterImage="/gallery/detailing/after.jpg"
                className="aspect-[4/5]"
              />
            </div>
            <div>
              <p className="text-cyan-400 text-sm uppercase tracking-wider mb-4 font-semibold">
                Interior Deep Clean
              </p>
              <BeforeAfterSlider
                beforeImage="/gallery/detailing/before1.jpg"
                afterImage="/gallery/detailing/after1.jpg"
                className="aspect-[4/5]"
              />
            </div>
            <div>
              <p className="text-cyan-400 text-sm uppercase tracking-wider mb-4 font-semibold">
                Full Interior Detail
              </p>
              <BeforeAfterSlider
                beforeImage="/gallery/detailing/before2.jpg"
                afterImage="/gallery/detailing/after2.jpg"
                className="aspect-[4/5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Packages Preview */}
      <section className="section-padding" data-testid="detailing-packages">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-4">
              Detailing Packages
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Choose the package that fits your needs. All packages include mobile service to your location in Fresno and surrounding areas.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="pricing-card bg-neutral-900 border border-white/10 p-8"
            >
              <div className="text-center pb-6 border-b border-white/10 mb-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-wider mb-2">
                  Maintenance Refresh
                </h3>
                <div className="font-accent">
                  <span className="text-4xl font-bold text-cyan-400">$99</span>
                  <span className="text-gray-500">/starting</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-6">
                Interior wipe down, vacuum, blow out + full exterior hand wash, wheels, and spray sealant.
              </p>
              <button 
                onClick={() => openBooking(1)}
                className="w-full py-3 border border-cyan-500/50 text-cyan-400 font-semibold uppercase tracking-wider hover:bg-cyan-500/10 transition-all"
              >
                Book Now
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="pricing-card featured bg-neutral-900 border border-cyan-500 p-8 relative"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-black text-xs font-bold uppercase tracking-wider px-3 py-1">
                Popular
              </div>
              <div className="text-center pb-6 border-b border-white/10 mb-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-wider mb-2">
                  Radiant Full Detail
                </h3>
                <div className="font-accent">
                  <span className="text-4xl font-bold text-cyan-400">$150</span>
                  <span className="text-gray-500">/starting</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-6">
                Full interior deep clean + complete exterior detail. Perfect balance of thorough cleaning and maintenance.
              </p>
              <button 
                onClick={() => openBooking(2)}
                className="w-full py-3 bg-cyan-500 text-black font-semibold uppercase tracking-wider hover:bg-cyan-400 transition-all"
              >
                Book Now
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="pricing-card bg-neutral-900 border border-white/10 p-8"
            >
              <div className="text-center pb-6 border-b border-white/10 mb-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-wider mb-2">
                  Elite Lustre
                </h3>
                <div className="font-accent">
                  <span className="text-4xl font-bold text-cyan-400">$230</span>
                  <span className="text-gray-500">/starting</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-6">
                Deep full detail + clay bar + 3-6 month ceramic coating + steam cleaning + premium air freshener.
              </p>
              <button 
                onClick={() => openBooking(3)}
                className="w-full py-3 border border-cyan-500/50 text-cyan-400 font-semibold uppercase tracking-wider hover:bg-cyan-500/10 transition-all"
              >
                Book Now
              </button>
            </motion.div>
          </div>

          <div className="text-center mt-8">
            <Link 
              to="/#packages"
              className="text-cyan-400 hover:text-cyan-300 transition-colors inline-flex items-center gap-2"
            >
              View Full Package Details <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-padding bg-[#0a0a0a]" data-testid="detailing-areas">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-6">
                Mobile Detailing Service Areas
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                Noble Detailing provides professional mobile auto detailing throughout Central California. We bring everything needed to your location - no water or power connection required from you.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                {['Fresno', 'Madera', 'Clovis', 'Chowchilla', 'Sanger', 'Selma', 'Visalia', 'Hanford'].map((city) => (
                  <div key={city} className="flex items-center gap-2 text-gray-300">
                    <MapPin className="w-5 h-5 text-cyan-500" />
                    <span>{city}, CA</span>
                  </div>
                ))}
              </div>

              <ContactDropdown 
                label="Contact Us" 
                className="btn-primary"
                testId="detailing-service-area-contact"
              />
            </div>

            <div className="bg-neutral-900 border border-white/10 p-8">
              <h3 className="font-heading text-xl font-semibold uppercase tracking-wider mb-6">
                What We Bring
              </h3>
              <ul className="space-y-4">
                {[
                  'Self-contained water system',
                  'Generator/power supply',
                  'Professional-grade vacuums',
                  'Steam cleaning equipment',
                  'Premium cleaning products',
                  'Microfiber towels & applicators',
                  'Polishing machines',
                  'Extraction equipment'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ items={faqItems} title="Mobile Detailing FAQ - Fresno" />

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-b from-[#0a0a0a] to-black" data-testid="detailing-cta-section">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-6">
            Ready to Schedule Your Detail?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Book your mobile detailing appointment today. We'll come to your home or office in Fresno, Madera, Clovis, and surrounding areas.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <ContactDropdown 
              label="Contact Us" 
              className="btn-primary"
              testId="detailing-bottom-contact"
            />
            <Link 
              to="/ceramic-coating-fresno"
              className="btn-secondary"
            >
              Add Ceramic Coating
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};
